import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
          <div className="bg-white p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t('contact.form.service')}
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer"
                >
                  <option value="">{t('contact.form.service.placeholder')}</option>
                  <option value="tax-prep">{t('contact.form.service.tax')}</option>
                  <option value="planning">{t('contact.form.service.planning')}</option>
                  <option value="patronage">{t('contact.form.service.patronage')}</option>
                  <option value="irs-rep">{t('contact.form.service.irs')}</option>
                  <option value="business">{t('contact.form.service.business')}</option>
                  <option value="accounting">{t('contact.form.service.accounting')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {t('contact.form.submit')}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 text-green-700 px-4 py-3 rounded-xl text-sm">
                  Grazie! Ti risponderemo presto.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">
                  Qualcosa è andato storto. Riprova.
                </div>
              )}
            </form>
          </div>

          <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-12 text-white relative overflow-hidden">
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-teal-500/20 rounded-full -mr-32 -mb-32"></div>
            <div className="absolute top-0 left-0 w-48 h-48 bg-teal-800/20 rounded-full -ml-24 -mt-24"></div>
            
            <div className="relative z-10 space-y-12">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('contact.info.title')}</h3>
                  <p className="text-white/90">
                    <a href="https://maps.app.goo.gl/FP3gD3KKJKpQjWmj8" target="_blank" rel="nofollow noopener noreferrer" className="hover:underline cursor-pointer">
                      Via Marengo, 6 - Chiari - Brescia
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                  <i className="ri-mail-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Contatti</h3>
                  <p className="text-white/90">stefania.navoni@enasc.eu<br />Tel: 030 712 316<br />Mobile: 375 693 5807</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Orari</h3>
                  <p className="text-white/90">
                    {t('contact.info.hours')}<br />
                    {t('contact.info.saturday')}
                  </p>
                </div>
              </div>

              <div className="pt-8 border-t border-white/20">
                <h3 className="text-xl font-bold mb-4">Social</h3>
                <div className="flex gap-4">
                  <button className="w-12 h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-white/10 transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-white/10 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-xl"></i>
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-white/10 transition-colors cursor-pointer">
                    <i className="ri-twitter-fill text-xl"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
